import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const TYPE_COLORS = {
  call: 'bg-blue-50 text-blue-700 border-blue-200',
  site_visit: 'bg-green-50 text-green-700 border-green-200',
  demo: 'bg-purple-50 text-purple-700 border-purple-200',
  follow_up: 'bg-orange-50 text-orange-700 border-orange-200',
};
const TYPE_ICONS = {
  call: 'fa-phone',
  site_visit: 'fa-house',
  demo: 'fa-display',
  follow_up: 'fa-arrow-rotate-right',
};
const STATUS_COLORS = {
  scheduled: 'bg-primary/10 text-primary',
  completed: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-700',
  no_show: 'bg-gray-100 text-gray-600',
};

const VIEWS = ['month', 'week', 'list'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getMonthMatrix(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const days = getDaysInMonth(year, month);
  const matrix = [];
  let day = 1 - firstDay;
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++, day++) {
      row.push(day);
    }
    matrix.push(row);
    if (day > days) break;
  }
  return matrix;
}

export default function Appointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [showCreate, setShowCreate] = useState(false);
  const [leads, setLeads] = useState([]);
  const [calDate, setCalDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const [pickedLead, setPickedLead] = useState(null);

  const today = new Date();
  const defaultDateTime = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  defaultDateTime.setHours(10, 0, 0, 0);
  const toLocal = (d) => {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    lead_id: '',
    title: '',
    scheduled_at: toLocal(defaultDateTime),
    duration_minutes: 60,
    location: '',
    notes: '',
    appointment_type: 'call',
  });

  const fetchAll = useCallback(async () => {
    try {
      const [apRes, upRes, ldRes] = await Promise.all([
        api.get('/api/appointments'),
        api.get('/api/appointments/upcoming?days=14'),
        api.get('/api/leads?limit=100'),
      ]);
      setAppointments(apRes.data.appointments || []);
      setUpcoming(upRes.data.appointments || []);
      setLeads(ldRes.data.leads || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.lead_id || !form.title) { toast.error('Select a lead and enter a title'); return; }
    try {
      const selectedLead = pickedLead || leads.find(l => l.id === form.lead_id);
      await api.post('/api/appointments', { ...form, lead_name: selectedLead?.name || '' });
      toast.success('Appointment scheduled!');
      setShowCreate(false);
      setPickedLead(null);
      setForm({ lead_id: '', title: '', scheduled_at: toLocal(defaultDateTime), duration_minutes: 60, location: '', notes: '', appointment_type: 'call' });
      fetchAll();
    } catch { toast.error('Failed to schedule'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/api/appointments/${id}`, { status });
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      setUpcoming(prev => prev.filter(a => a.id !== id || status === 'scheduled'));
      toast.success(`Marked as ${status}`);
    } catch { toast.error('Update failed'); }
  };

  const deleteAppt = async (id) => {
    try {
      await api.delete(`/api/appointments/${id}`);
      setAppointments(prev => prev.filter(a => a.id !== id));
      setUpcoming(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  // Calendar helpers
  const apptsByDay = {};
  appointments.forEach(a => {
    const day = a.scheduled_at?.slice(0, 10);
    if (day) {
      if (!apptsByDay[day]) apptsByDay[day] = [];
      apptsByDay[day].push(a);
    }
  });

  const matrix = getMonthMatrix(calDate.getFullYear(), calDate.getMonth());
  const monthName = calDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const ApptCard = ({ a, compact = false }) => (
    <div
      key={a.id}
      className={`card-interactive p-4 animate-slide-up ${compact ? 'p-3' : ''}`}
      data-testid={`appointment-${a.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border text-xs ${TYPE_COLORS[a.appointment_type] || TYPE_COLORS.call}`}>
            <i className={`fa-solid ${TYPE_ICONS[a.appointment_type] || 'fa-calendar'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-text-primary">{a.title}</p>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[a.status] || ''}`}>{a.status}</span>
            </div>
            <button
              className="text-xs text-primary hover:underline font-medium mt-0.5"
              onClick={() => navigate(`/leads/${a.lead_id}`)}
            >
              {a.lead_name}
            </button>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs text-text-muted">
                <i className="fa-solid fa-clock mr-1" />
                {new Date(a.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {a.duration_minutes && ` · ${a.duration_minutes}min`}
              </span>
              {a.location && <span className="text-xs text-text-muted"><i className="fa-solid fa-location-dot mr-1" />{a.location}</span>}
            </div>
            {a.notes && !compact && <p className="text-xs text-text-muted mt-1 italic">{a.notes}</p>}
          </div>
        </div>
        {a.status === 'scheduled' && (
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => updateStatus(a.id, 'completed')} title="Mark Complete" className="p-1.5 rounded-lg text-text-muted hover:text-green-600 hover:bg-green-50 transition-all text-xs">
              <i className="fa-solid fa-circle-check" />
            </button>
            <button onClick={() => updateStatus(a.id, 'cancelled')} title="Cancel" className="p-1.5 rounded-lg text-text-muted hover:text-orange-500 hover:bg-orange-50 transition-all text-xs">
              <i className="fa-solid fa-ban" />
            </button>
            <button onClick={() => updateStatus(a.id, 'no_show')} title="No Show" className="p-1.5 rounded-lg text-text-muted hover:text-gray-500 hover:bg-gray-100 transition-all text-xs">
              <i className="fa-solid fa-ghost" />
            </button>
            <button onClick={() => deleteAppt(a.id)} title="Delete" className="p-1.5 rounded-lg text-text-muted hover:text-destructive hover:bg-destructive-light transition-all text-xs">
              <i className="fa-solid fa-trash" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Appointments</h1>
          <p className="text-text-muted text-sm mt-1">Schedule and manage all your lead meetings</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-xl bg-surface-alt border border-border">
            {VIEWS.map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${view === v ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
              >
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs py-2.5" data-testid="new-appointment-btn">
            <i className="fa-solid fa-plus mr-1.5" />New Appointment
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: 'Total', v: appointments.length, icon: 'fa-calendar', c: 'primary' },
          { l: 'Upcoming', v: upcoming.length, icon: 'fa-clock', c: 'accent' },
          { l: 'Completed', v: appointments.filter(a => a.status === 'completed').length, icon: 'fa-circle-check', c: 'primary' },
          { l: 'No Show', v: appointments.filter(a => a.status === 'no_show').length, icon: 'fa-ghost', c: 'secondary' },
        ].map((s, i) => (
          <div key={s.l} className="card-interactive p-4 animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center gap-2 mb-2">
              <i className={`fa-solid ${s.icon} text-${s.c} text-xs`} />
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{s.l}</span>
            </div>
            <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{s.v}</p>
          </div>
        ))}
      </div>

      {/* Upcoming strip */}
      {upcoming.length > 0 && (
        <div className="card-interactive p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-text-muted mb-3">
            <i className="fa-solid fa-bolt text-primary mr-1.5" />Upcoming (Next 14 Days)
          </h3>
          <div className="space-y-2">
            {upcoming.slice(0, 5).map(a => <ApptCard key={a.id} a={a} compact />)}
          </div>
        </div>
      )}

      {/* Calendar View */}
      {view === 'month' && (
        <div className="card-interactive p-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCalDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="p-2 rounded-lg hover:bg-surface-alt transition-all">
              <i className="fa-solid fa-chevron-left text-text-muted text-sm" />
            </button>
            <h3 className="font-heading text-base font-semibold text-text-primary">{monthName}</h3>
            <button onClick={() => setCalDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="p-2 rounded-lg hover:bg-surface-alt transition-all">
              <i className="fa-solid fa-chevron-right text-text-muted text-sm" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="text-center text-[10px] font-bold uppercase text-text-muted py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {matrix.flat().map((day, i) => {
              const isCurrentMonth = day >= 1 && day <= getDaysInMonth(calDate.getFullYear(), calDate.getMonth());
              const dateStr = isCurrentMonth ? `${calDate.getFullYear()}-${String(calDate.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
              const dayAppts = dateStr ? (apptsByDay[dateStr] || []) : [];
              const isToday = dateStr === today.toISOString().slice(0,10);
              return (
                <div
                  key={i}
                  onClick={() => dateStr && setSelectedDay(selectedDay === dateStr ? null : dateStr)}
                  className={`min-h-[72px] p-1.5 rounded-xl border transition-all cursor-pointer ${isCurrentMonth ? 'border-border hover:border-primary/30 hover:bg-surface-alt/50' : 'border-transparent opacity-30'} ${isToday ? 'border-primary/40 bg-primary/5' : ''} ${selectedDay === dateStr ? 'border-primary bg-primary/5' : ''}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mb-1 ${isToday ? 'bg-primary text-white font-bold' : 'text-text-secondary'}`}>
                    {isCurrentMonth ? day : ''}
                  </div>
                  {dayAppts.slice(0, 2).map(a => (
                    <div key={a.id} className={`text-[9px] truncate px-1 py-0.5 rounded mb-0.5 font-medium ${TYPE_COLORS[a.appointment_type] || 'bg-primary/10 text-primary'}`}>
                      {a.title}
                    </div>
                  ))}
                  {dayAppts.length > 2 && <div className="text-[9px] text-text-muted">+{dayAppts.length - 2} more</div>}
                </div>
              );
            })}
          </div>
          {selectedDay && apptsByDay[selectedDay] && (
            <div className="mt-4 space-y-2 border-t border-border pt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">
                {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              {apptsByDay[selectedDay].map(a => <ApptCard key={a.id} a={a} />)}
            </div>
          )}
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="card-interactive p-6">
          <p className="text-sm font-semibold text-text-primary mb-4">This Week's Appointments</p>
          {appointments.filter(a => {
            const d = new Date(a.scheduled_at);
            const start = new Date(); start.setHours(0,0,0,0);
            start.setDate(start.getDate() - start.getDay());
            const end = new Date(start); end.setDate(end.getDate() + 7);
            return d >= start && d < end;
          }).length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <i className="fa-regular fa-calendar text-3xl opacity-20 block mb-2" />
              <p className="text-sm">No appointments this week</p>
            </div>
          ) : (
            <div className="space-y-2">
              {appointments.filter(a => {
                const d = new Date(a.scheduled_at);
                const start = new Date(); start.setHours(0,0,0,0);
                start.setDate(start.getDate() - start.getDay());
                const end = new Date(start); end.setDate(end.getDate() + 7);
                return d >= start && d < end;
              }).map(a => <ApptCard key={a.id} a={a} />)}
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="space-y-2">
          {appointments.length === 0 ? (
            <div className="card-interactive py-20 text-center">
              <i className="fa-regular fa-calendar text-4xl text-text-muted opacity-20 block mb-4" />
              <p className="text-text-muted font-medium">No appointments yet</p>
              <p className="text-xs text-text-muted mt-1">Schedule your first appointment with a lead</p>
            </div>
          ) : (
            appointments.map((a, i) => (
              <div key={a.id} style={{ animationDelay: `${i * 30}ms` }}>
                <ApptCard a={a} />
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()} data-testid="create-appointment-modal">
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">Schedule Appointment</h2>
            <p className="text-sm text-text-muted mb-5">Book a meeting, call, or site visit with a lead</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Lead</label>
                <LeadSearchPicker
                  selected={pickedLead}
                  recent={leads}
                  onSelect={(lead) => { setPickedLead(lead); setForm({ ...form, lead_id: lead?.id || '' }); }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Title</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field h-9 text-sm" placeholder="Initial consultation call" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Type</label>
                  <select value={form.appointment_type} onChange={e => setForm({...form, appointment_type: e.target.value})} className="input-field h-9 text-sm">
                    <option value="call">Phone Call</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="demo">Demo</option>
                    <option value="follow_up">Follow-up</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Duration</label>
                  <select value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value)})} className="input-field h-9 text-sm">
                    {[15,30,45,60,90,120].map(m => <option key={m} value={m}>{m} min</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Date & Time</label>
                <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} required className="input-field h-9 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Location (Optional)</label>
                <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="input-field h-9 text-sm" placeholder="Zoom / Phone / Address" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="input-field h-auto py-2.5 resize-none text-sm" placeholder="Preparation notes..." />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="save-appointment-btn">
                  <i className="fa-solid fa-calendar-plus mr-1.5" />Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadSearchPicker({ selected, recent, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResults((recent || []).slice(0, 8)); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/api/leads', { params: { search: term, limit: 10, sort_by: 'score', sort_order: 'desc' } });
        setResults(r.data.leads || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, recent]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 border border-border rounded-lg px-3 h-9 bg-surface-alt" data-testid="appt-lead-selected">
        <i className="fa-solid fa-user-check text-primary text-xs" />
        <span className="text-sm text-text-primary truncate flex-1">{selected.name}{selected.city ? ` — ${selected.city}${selected.state ? `, ${selected.state}` : ''}` : ''}</span>
        <button type="button" onClick={() => { onSelect(null); setQ(''); setOpen(true); }} className="text-text-muted hover:text-destructive text-xs" data-testid="appt-lead-clear"><i className="fa-solid fa-xmark" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-xs" />
      <input
        type="text" value={q} onFocus={() => setOpen(true)} onChange={e => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search leads by name, address, city, ZIP..."
        className="input-field h-9 text-sm pl-8" data-testid="appt-lead-search-input"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-surface border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="appt-lead-results">
          {searching && <p className="px-3 py-2 text-xs text-text-muted"><i className="fa-solid fa-circle-notch fa-spin mr-1.5" />Searching…</p>}
          {!searching && results.length === 0 && <p className="px-3 py-2 text-xs text-text-muted">No leads match "{q}"</p>}
          {results.map(l => (
            <button
              key={l.id} type="button"
              onClick={() => { onSelect(l); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-surface-alt border-b border-border/50 last:border-0"
              data-testid="appt-lead-option"
            >
              <p className="text-sm text-text-primary font-medium truncate">{l.name}</p>
              <p className="text-[11px] text-text-muted truncate">{[l.address, l.city, l.state, l.zip].filter(Boolean).join(', ')} · score {l.score}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
